/**
 * CENTRALIZED AFFILIATE LINKS CONFIGURATION
 * 
 * UPDATE INSTRUCTIONS:
 * 1. Replace "YOUR_AFFILIATE_ID" with your actual affiliate IDs
 * 2. Add UTM parameters for tracking (?utm_source=itssold&utm_medium=referral&utm_campaign=fsbo)
 * 3. Test each link after updating
 * 
 * This file controls ALL affiliate links across the entire site.
 * Update once here, changes apply everywhere!
 */

// Base UTM parameters for all links
const UTM_PARAMS = '?utm_source=itssold&utm_medium=referral&utm_campaign=fsbo';

export const AFFILIATE_LINKS = {
  
  // ============================================
  // HOME STAGING
  // ============================================
  staging: {
    virtualStagingSolutions: {
      name: 'Virtual Staging Solutions',
      url: 'https://virtualstagingsolutions.com/affiliate/YOUR_AFFILIATE_ID',
      commission: '25% lifetime',
      commissionAmount: 37.50, // average per referral
      category: 'staging',
      local: false,
      priority: 1
    },
    completeStagingDesign: {
      name: 'Complete Staging & Design',
      url: 'https://www.completestaginganddesign.com' + UTM_PARAMS,
      commission: '15-20%',
      commissionAmount: 250, // average
      category: 'staging',
      local: true,
      priority: 1
    },
    dfwStaging: {
      name: 'DFW Staging',
      url: 'https://www.dfw-staging.com' + UTM_PARAMS,
      commission: '$50/lead + 10%',
      commissionAmount: 100, // average
      category: 'staging',
      local: true,
      priority: 1
    },
    charterFurniture: {
      name: 'Charter Furniture Solutions',
      url: 'https://www.charterfurnituresolutions.com/home-staging' + UTM_PARAMS,
      commission: '10-15%',
      commissionAmount: 400, // average
      category: 'staging',
      local: true,
      priority: 2
    }
  },

  // ============================================
  // CLEANING SERVICES
  // ============================================
  cleaning: {
    mopAndBroom: {
      name: 'The Mop & Broom',
      url: 'https://www.themopandbroom.com/services/real-estate-agents' + UTM_PARAMS,
      commission: '$25-50',
      commissionAmount: 37.50,
      category: 'cleaning',
      local: true,
      priority: 1
    },
    merryMaidsDFW: {
      name: 'Merry Maids DFW',
      url: 'https://www.merrymaids.com/home-cleaners-dallas-tx' + UTM_PARAMS,
      commission: '10-20%',
      commissionAmount: 50,
      category: 'cleaning',
      local: true,
      priority: 1
    },
    zerorezDFW: {
      name: 'Zerorez DFW',
      url: 'https://www.zerorez.com/affiliates/YOUR_AFFILIATE_ID',
      commission: '$40 new / $30 return',
      commissionAmount: 40,
      category: 'cleaning',
      local: true,
      priority: 1
    },
    topLocalMaids: {
      name: 'Top Local Maids',
      url: 'https://toplocalmaids.com/affiliate/YOUR_AFFILIATE_ID',
      commission: '$3/lead + 10%',
      commissionAmount: 30,
      category: 'cleaning',
      local: false,
      priority: 2
    }
  },

  // ============================================
  // MOVING SERVICES
  // ============================================
  moving: {
    muvr: {
      name: 'Muvr',
      url: 'https://www.muvr.io/business/affiliate-program/YOUR_AFFILIATE_ID',
      commission: '$100 per booking',
      commissionAmount: 100,
      category: 'moving',
      local: false,
      priority: 1
    },
    elementMoving: {
      name: 'Element Moving & Storage',
      url: 'https://elementmoving.com/affiliate-program-element-moving/YOUR_AFFILIATE_ID',
      commission: 'Competitive',
      commissionAmount: 75,
      category: 'moving',
      local: false,
      priority: 2
    }
  },

  // ============================================
  // LAND SURVEYING
  // ============================================
  surveying: {
    fullerEngineering: {
      name: 'Fuller Engineering',
      url: 'https://www.dfwsurvey.com' + UTM_PARAMS,
      commission: '$50-100',
      commissionAmount: 75,
      category: 'surveying',
      local: true,
      priority: 1
    },
    gsiGeomatic: {
      name: 'GSI Geomatic Solutions',
      url: 'https://www.gsisurvey.com' + UTM_PARAMS,
      commission: '$75',
      commissionAmount: 75,
      category: 'surveying',
      local: true,
      priority: 1
    },
    votexSurveying: {
      name: 'Votex Surveying',
      url: 'https://www.votexsurveying.com' + UTM_PARAMS,
      commission: '15%',
      commissionAmount: 200,
      category: 'surveying',
      local: true,
      priority: 2
    }
  },

  // ============================================
  // MORTGAGE LENDING
  // ============================================
  mortgage: {
    trussFinancial: {
      name: 'Truss Financial Group',
      url: 'https://www.trussfinancialgroup.com/affiliate/program/agent/YOUR_AFFILIATE_ID',
      commission: 'Up to $200',
      commissionAmount: 200,
      category: 'mortgage',
      local: true,
      priority: 1
    },
    loneStarFinancing: {
      name: 'Lone Star Financing',
      url: 'https://www.lonestarfinancing.com/dallas' + UTM_PARAMS,
      commission: '$50-150',
      commissionAmount: 100,
      category: 'mortgage',
      local: true,
      priority: 1
    },
    tuttleGroup: {
      name: 'The Tuttle Group',
      url: 'https://www.thetuttlegroup.com' + UTM_PARAMS,
      commission: '$100+',
      commissionAmount: 125,
      category: 'mortgage',
      local: true,
      priority: 2
    }
  },

  // ============================================
  // HOME INSURANCE
  // ============================================
  insurance: {
    policygeniusDFW: {
      name: 'Policygenius DFW',
      url: 'https://www.policygenius.com/homeowners-insurance/texas/dallas/YOUR_AFFILIATE_ID',
      commission: '$20-50',
      commissionAmount: 35,
      category: 'insurance',
      local: true,
      priority: 1
    },
    libertyMutual: {
      name: 'Liberty Mutual',
      url: 'https://www.libertymutual.com/YOUR_AFFILIATE_ID',
      commission: '$10',
      commissionAmount: 10,
      category: 'insurance',
      local: true,
      priority: 2
    },
    lemonade: {
      name: 'Lemonade',
      url: 'https://www.lemonade.com/YOUR_AFFILIATE_ID',
      commission: '$25.50',
      commissionAmount: 25.50,
      category: 'insurance',
      local: false,
      priority: 2
    }
  },

  // ============================================
  // HOME INSPECTION
  // ============================================
  inspection: {
    ashiDirectory: {
      name: 'ASHI Inspector Directory',
      url: 'https://www.homeinspector.org/find-a-home-inspector' + UTM_PARAMS,
      commission: 'N/A',
      commissionAmount: 0,
      category: 'inspection',
      local: false,
      priority: 1
    },
    interNACHI: {
      name: 'InterNACHI Directory',
      url: 'https://www.nachi.org/find-inspector/' + UTM_PARAMS,
      commission: 'N/A',
      commissionAmount: 0,
      category: 'inspection',
      local: false,
      priority: 1
    }
  }
};

/**
 * Helper function to get link with tracking
 * @param {string} category - Category key (e.g., 'staging')
 * @param {string} service - Service key (e.g., 'virtualStagingSolutions')
 * @param {string} source - Optional custom source for tracking
 */
export const getAffiliateLink = (category, service, source = null) => {
  const link = AFFILIATE_LINKS[category]?.[service];
  if (!link) return '#';
  
  let url = link.url;
  
  // Add custom source if provided
  if (source) {
    const separator = url.includes('?') ? '&' : '?';
    url += `${separator}source=${source}`;
  }
  
  return url;
};

/**
 * Get all services for a category
 */
export const getServicesByCategory = (category) => {
  return Object.values(AFFILIATE_LINKS[category] || {});
};

/**
 * Get high-priority services (priority 1)
 */
export const getHighPriorityServices = () => {
  const allServices = [];
  Object.keys(AFFILIATE_LINKS).forEach(category => {
    Object.values(AFFILIATE_LINKS[category]).forEach(service => {
      if (service.priority === 1) {
        allServices.push(service);
      }
    });
  });
  return allServices;
};

/**
 * Get DFW local services only
 */
export const getDFWLocalServices = () => {
  const allServices = [];
  Object.keys(AFFILIATE_LINKS).forEach(category => {
    Object.values(AFFILIATE_LINKS[category]).forEach(service => {
      if (service.local) {
        allServices.push(service);
      }
    });
  });
  return allServices;
};

/**
 * Calculate potential commission for service recommendations
 */
export const calculatePotentialCommission = (services) => {
  return services.reduce((total, service) => {
    return total + (service.commissionAmount || 0);
  }, 0);
};

/**
 * Service recommendations based on user journey stage
 */
export const getRecommendedServices = (stage) => {
  const recommendations = {
    'listing-created': ['staging', 'cleaning'], // Just created listing
    'pre-photos': ['staging', 'cleaning'], // Before taking photos
    'active-listing': ['cleaning', 'moving'], // Listing is active
    'offer-received': ['surveying', 'inspection'], // Buyer made offer
    'closing-soon': ['moving', 'cleaning'], // Within 30 days of closing
    'buyer-inquiry': ['inspection', 'mortgage', 'insurance'] // Buyer perspective
  };
  
  const serviceKeys = recommendations[stage] || [];
  const services = [];
  
  serviceKeys.forEach(category => {
    services.push(...getServicesByCategory(category));
  });
  
  return services.sort((a, b) => a.priority - b.priority);
};

export default AFFILIATE_LINKS;
